import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import WorkflowsList from './WorkflowsList';
import ViewWorkflow from './ViewWorkflow';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './Workflows.css';

function Workflows() {
  const { t } = useTranslation('production');
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'view', 'edit', 'create'

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: t('workflows.toolbar.titles.workflows'),
          buttons: [
            {
              label: t('workflows.toolbar.buttons.newWorkflow'),
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: t('workflows.toolbar.buttons.refresh'),
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: t('workflows.toolbar.titles.viewWorkflow'),
          showBack: true,
          onBack: handleBack,
          buttons: [
            {
              label: t('workflows.toolbar.buttons.edit'),
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: t('workflows.toolbar.buttons.delete'),
              icon: '🗑️',
              onClick: handleDelete,
              variant: 'danger'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: t('workflows.toolbar.titles.editWorkflow'),
          showBack: false,
          buttons: [
            {
              label: t('workflows.toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('workflows.toolbar.buttons.save'),
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: t('workflows.toolbar.titles.newWorkflow'),
          showBack: false,
          buttons: [
            {
              label: t('workflows.toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('workflows.toolbar.buttons.create'),
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode, t]);

  const handleSelectWorkflow = (workflowId) => {
    setSelectedWorkflowId(workflowId);
    setViewMode('view');
  };

  const handleNew = () => {
    setSelectedWorkflowId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewWorkflow component
    if (window.viewWorkflowRef && window.viewWorkflowRef.handleDelete) {
      window.viewWorkflowRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewWorkflow component
    if (window.viewWorkflowRef && window.viewWorkflowRef.handleSave) {
      window.viewWorkflowRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedWorkflowId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedWorkflowId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in WorkflowsList
    if (window.workflowsListRef && window.workflowsListRef.fetchWorkflows) {
      window.workflowsListRef.fetchWorkflows();
    }
  };

  const handleWorkflowSaved = (workflow) => {
    setSelectedWorkflowId(workflow.workflow_id);
    setViewMode('view');
  };

  return (
    <div className="workflows-page">
      <ResizablePanels
        leftPanel={
          <WorkflowsList
            ref={ref => window.workflowsListRef = ref}
            onSelectWorkflow={handleSelectWorkflow}
            selectedWorkflowId={selectedWorkflowId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewWorkflow
              ref={ref => window.viewWorkflowRef = ref}
              workflowId={selectedWorkflowId}
              onBack={handleBack}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onSave={handleWorkflowSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default Workflows;